/**
 * AddServicePointModal - Lägg till en manuell servicepunkt eller anpassad servicetyp.
 */

import React, { useState, useEffect } from 'react';
import { XMarkIcon, PlusIcon } from '@heroicons/react/24/outline';
import { useTranslation } from '@/store/languageStore';
import { serviceApi, ServiceType } from '@/services/service/serviceApi';

import DateInput from '@/components/common/DateInput';

interface AddServicePointModalProps {
  vehicleId: number;
  onClose: () => void;
  onCreated: () => void;
}

const AddServicePointModal: React.FC<AddServicePointModalProps> = ({ vehicleId, onClose, onCreated }) => {
  const { t } = useTranslation();
  const [serviceTypes, setServiceTypes] = useState<ServiceType[]>([]);
  const [mode, setMode] = useState<'existing' | 'custom'>('existing');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Existing type
  const [selectedTypeId, setSelectedTypeId] = useState('');

  // Custom type
  const [customName, setCustomName] = useState('');
  const [customCategory, setCustomCategory] = useState<'MAINTENANCE' | 'CUSTOM'>('CUSTOM');

  // Common
  const [intervalKm, setIntervalKm] = useState('');
  const [intervalMonths, setIntervalMonths] = useState('');
  const [warningKm, setWarningKm] = useState('3000');
  const [warningDays, setWarningDays] = useState('30');
  const [lastPerformedDate, setLastPerformedDate] = useState('');
  const [lastPerformedKm, setLastPerformedKm] = useState('');

  useEffect(() => {
    serviceApi.getServiceTypes().then(setServiceTypes).catch(console.error);
  }, []);

  const parseUnsignedInt = (raw: string, label: string, max: number, min = 0): number | undefined => {
    const trimmed = raw.trim();
    if (!trimmed) return undefined;
    const n = Number(trimmed);
    if (!Number.isInteger(n) || n < min) {
      throw new Error(
        min <= 0 ? `${label} får inte vara negativt` : `${label} måste vara ett heltal minst ${min}`
      );
    }
    if (n > max) {
      throw new Error(`${label} får vara högst ${max}`);
    }
    return n;
  };

  const handleSubmit = async () => {
    setLoading(true);
    setError(null);
    try {
      const parsedIntervalKm = parseUnsignedInt(intervalKm, t('service.intervalKm') || 'Intervall (km)', 2_000_000, 1);
      const parsedIntervalMonths = parseUnsignedInt(
        intervalMonths,
        t('service.intervalMonths') || 'Intervall (mån)',
        600,
        1
      );
      if (parsedIntervalKm == null && parsedIntervalMonths == null) {
        throw new Error(t('service.intervalHint') || 'Ange minst ett intervall – km, tid (mån) eller båda.');
      }

      const data: Record<string, any> = {
        source: 'MANUAL',
        intervalKm: parsedIntervalKm,
        intervalMonths: parsedIntervalMonths,
        warningKm: parseUnsignedInt(warningKm, t('service.warningKm') || 'Varning km före', 100_000) ?? 3000,
        warningDays: parseUnsignedInt(warningDays, t('service.warningDays') || 'Varning dagar före', 3650) ?? 30,
        lastPerformedDate: lastPerformedDate || undefined,
        lastPerformedKm: lastPerformedKm.trim()
          ? parseUnsignedInt(lastPerformedKm, t('service.lastPerformedKm') || 'Senast utförd (km)', 2_000_000)
          : undefined,
      };

      if (mode === 'existing') {
        data.serviceTypeId = selectedTypeId;
      } else {
        data.name = customName;
        data.category = customCategory;
      }

      await serviceApi.createServicePoint(vehicleId, data);
      onCreated();
      onClose();
    } catch (err: unknown) {
      console.error('Failed to create service point:', err);
      const axiosErr = err as { response?: { status?: number; data?: { error?: string; code?: string } }; message?: string };
      const status = axiosErr?.response?.status;
      const serverMsg = axiosErr?.response?.data?.error;
      const code = axiosErr?.response?.data?.code;
      if (!axiosErr?.response && err instanceof Error) {
        setError(err.message);
      } else if (status === 403) {
        setError(
          serverMsg ||
            (t('service.noCreatePermission') ||
              'Du saknar behörighet att lägga till servicepunkter (CREATE).')
        );
      } else if (status === 400 && serverMsg) {
        setError(serverMsg);
      } else if (serverMsg) {
        setError(code ? `${serverMsg} (${code})` : serverMsg);
      } else {
        setError(t('service.saveFailed') || 'Kunde inte spara servicepunkt. Försök igen.');
      }
    } finally {
      setLoading(false);
    }
  };

  const hasInterval = !!intervalKm.trim() || !!intervalMonths.trim();
  const hasType = mode === 'existing' ? !!selectedTypeId : !!customName.trim();
  const canSubmit = hasType && hasInterval;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md mx-4" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <h3 className="font-semibold text-gray-800 flex items-center gap-2">
            <PlusIcon className="h-5 w-5" />
            {t('service.addPoint') || 'Lägg till servicepunkt'}
          </h3>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded">
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        <div className="p-6 space-y-4">
          {/* Mode toggle */}
          <div className="flex gap-2">
            <button
              onClick={() => setMode('existing')}
              className={`flex-1 py-2 text-sm rounded-lg border ${mode === 'existing' ? 'bg-primary text-white border-primary' : 'hover:bg-gray-50'}`}
            >
              {t('service.existingType') || 'Befintlig typ'}
            </button>
            <button
              onClick={() => setMode('custom')}
              className={`flex-1 py-2 text-sm rounded-lg border ${mode === 'custom' ? 'bg-primary text-white border-primary' : 'hover:bg-gray-50'}`}
            >
              {t('service.customType') || 'Egen typ'}
            </button>
          </div>

          {mode === 'existing' ? (
            <div>
              <label className="text-xs text-textSecondary">{t('service.selectType') || 'Välj servicetyp'}</label>
              <select value={selectedTypeId} onChange={e => setSelectedTypeId(e.target.value)} className="w-full mt-1 px-3 py-2 border rounded text-sm">
                <option value="">{t('service.chooseType') || '-- Välj --'}</option>
                {serviceTypes.map(st => (
                  <option key={st.id} value={st.id}>
                    {st.name} {st.defaultIntervalKm ? `(${st.defaultIntervalKm.toLocaleString()} km)` : ''} {st.defaultIntervalMonths ? `(${st.defaultIntervalMonths} mån)` : ''}
                  </option>
                ))}
              </select>
            </div>
          ) : (
            <>
              <div>
                <label className="text-xs text-textSecondary">{t('service.typeName') || 'Namn på servicetyp'}</label>
                <input type="text" value={customName} onChange={e => setCustomName(e.target.value)} placeholder="T.ex. AC-service" className="w-full mt-1 px-3 py-2 border rounded text-sm" />
              </div>
              <div>
                <label className="text-xs text-textSecondary">{t('service.categoryLabel') || 'Kategori'}</label>
                <select value={customCategory} onChange={e => setCustomCategory(e.target.value as any)} className="w-full mt-1 px-3 py-2 border rounded text-sm">
                  <option value="CUSTOM">{t('service.categoryCustom') || 'Egen'}</option>
                  <option value="MAINTENANCE">{t('service.categoryMaintenance') || 'Underhåll'}</option>
                </select>
              </div>
            </>
          )}

          <p className="text-xs text-textTertiary">
            {t('service.intervalHint') || 'Ange minst ett intervall – km, tid (mån) eller båda.'}
          </p>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-textSecondary">{t('service.intervalKm') || 'Intervall (km)'}</label>
              <input type="number" min={1} max={2000000} value={intervalKm} onChange={e => setIntervalKm(e.target.value)} placeholder="30000" className="w-full mt-1 px-3 py-2 border rounded text-sm" />
            </div>
            <div>
              <label className="text-xs text-textSecondary">{t('service.intervalMonths') || 'Intervall (mån)'}</label>
              <input type="number" min={1} max={600} value={intervalMonths} onChange={e => setIntervalMonths(e.target.value)} placeholder="12" className="w-full mt-1 px-3 py-2 border rounded text-sm" />
            </div>
            <div>
              <label className="text-xs text-textSecondary">{t('service.warningKm') || 'Varning km före'}</label>
              <input type="number" min={0} max={100000} value={warningKm} onChange={e => setWarningKm(e.target.value)} className="w-full mt-1 px-3 py-2 border rounded text-sm" />
            </div>
            <div>
              <label className="text-xs text-textSecondary">{t('service.warningDays') || 'Varning dagar före'}</label>
              <input type="number" min={0} max={3650} value={warningDays} onChange={e => setWarningDays(e.target.value)} className="w-full mt-1 px-3 py-2 border rounded text-sm" />
            </div>
            <div>
              <label className="text-xs text-textSecondary">{t('service.lastPerformedDate') || 'Senast utförd (datum)'}</label>
              <DateInput value={lastPerformedDate} onChange={e => setLastPerformedDate(e.target.value)} className="w-full mt-1 px-3 py-2 border rounded text-sm" />
            </div>
            <div>
              <label className="text-xs text-textSecondary">{t('service.lastPerformedKm') || 'Senast utförd (km)'}</label>
              <input type="number" min={0} max={2000000} value={lastPerformedKm} onChange={e => setLastPerformedKm(e.target.value)} className="w-full mt-1 px-3 py-2 border rounded text-sm" />
            </div>
          </div>

          {error && (
            <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
              {error}
            </div>
          )}

          <button
            onClick={handleSubmit}
            disabled={loading || !canSubmit}
            className="w-full py-2.5 bg-primary text-white rounded-lg text-sm hover:bg-primary/90 disabled:opacity-50"
          >
            {loading ? (t('common.saving') || 'Sparar...') : (t('service.addPoint') || 'Lägg till servicepunkt')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddServicePointModal;
